import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "slider"
}

import { ref, computed, watch, onMounted } from 'vue'
import useColoredEntities from '@/composables/useColoredEntities'
import { ColorScaleConfig, controlledColorScale } from '@/utils/colorScale'
import MinMaxSliderFilter from './MinMaxSliderFilter.vue'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'

type Props = {
  fromColor: string
  toColor: string
  middleColor: string
  title: string
  min?: number
  max?: number
  step?: number
  format?: FormatOptions
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InteractiveColorLegend',
  props: {
    fromColor: {},
    toColor: {},
    middleColor: {},
    title: {},
    min: {},
    max: {},
    step: {},
    format: {}
  },
  setup(__props: any) {

const props = __props

const coloredEntities = useColoredEntities()

const key = ref(coloredEntities.currentKey)
const fromValue = ref(0)
const toValue = ref(0)

const toClosestUp = (value: number) => Math.ceil(value / step.value) * step.value
const toClosestDown = (value: number) => Math.floor(value / step.value) * step.value

const minRaw = computed(
  () =>
    props.min ??
    coloredEntities.data?.reduce((acc, curr) => Math.min(acc, curr.value), Infinity) ??
    0
)
const maxRaw = computed(
  () =>
    props.max ??
    coloredEntities.data?.reduce((acc, curr) => Math.max(acc, curr.value), -Infinity) ??
    0
)

const step = computed(
  () => props.step ?? Math.pow(10, Math.floor(Math.log10(maxRaw.value - minRaw.value))) / 100
)

const min = computed(() => toClosestDown(minRaw.value))
const max = computed(() =>
  toClosestUp(
    coloredEntities.data?.reduce((acc, curr) => Math.max(acc, curr.value), -Infinity) ?? 0
  )
)

const colorScale = computed<ColorScaleConfig | undefined>(() =>
  controlledColorScale(
    'STEP',
    [min.value, fromValue.value, toValue.value, max.value],
    [props.fromColor, props.middleColor, props.toColor]
  )
)

watch(
  colorScale,
  (colorScale) => {
    if (colorScale && coloredEntities.currentKey === key.value)
      coloredEntities.setColorScale(colorScale)
  },
  { immediate: true }
)

const percentile = (data: any[], p: number) => {
  const sorted = [...data].sort((a, b) => a.value - b.value)
  const pos = (sorted.length - 1) * p
  const base = Math.floor(pos)
  const rest = pos - base

  if (sorted[base + 1] !== undefined) {
    return sorted[base].value + rest * (sorted[base + 1].value - sorted[base].value)
  } else {
    return sorted[base].value
  }
}

watch(
  () => coloredEntities.data,
  (data) => {
    if (data && data.length > 0) {
      // Use 25th and 75th percentiles
      fromValue.value = toClosestDown(percentile(data, 0.25))
      toValue.value = toClosestUp(percentile(data, 0.75))
    }
  },
  { immediate: true }
)

onMounted(() => {
  scrollToBottom()
})

function scrollToBottom() {
  const rangeSlider = document.querySelector('.range-slider')
  if (rangeSlider) {
    rangeSlider.scrollTop = rangeSlider.scrollHeight // Scroll to the bottom of the container
  }
}

function updateValue(value: { from: number; to: number }) {
  fromValue.value = value.from
  toValue.value = value.to
}

return (_ctx: any,_cache: any) => {
  return (colorScale.value && max.value - min.value > step.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(MinMaxSliderFilter, {
          label: _ctx.title,
          labelType: "label",
          from: fromValue.value,
          to: toValue.value,
          min: min.value,
          max: max.value,
          step: step.value,
          fromColor: _ctx.fromColor,
          toColor: _ctx.toColor,
          middleColor: _ctx.middleColor,
          format: _ctx.format,
          onUpdateValue: updateValue
        }, null, 8, ["label", "from", "to", "min", "max", "step", "fromColor", "toColor", "middleColor", "format"])
      ]))
    : _createCommentVNode("", true)
}
}

})